import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit2, Power, PowerOff, RefreshCw, Database } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminBlockchainNetworksPage = () => {
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState(null);
  const [formData, setFormData] = useState({
    network_id: '',
    name: '',
    display_name: '',
    chain_id: '',
    currency_symbol: '',
    explorer_url: '',
    icon: '🔷',
    withdrawal_fee_estimate: '',
    confirmation_blocks: 12,
    is_enabled: true
  });

  useEffect(() => {
    fetchNetworks();
  }, []);

  const fetchNetworks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain-networks`, {
        credentials: 'include'
      });
      const data = await response.json();
      setNetworks(data.networks || []);
    } catch (error) {
      toast.error('Failed to load networks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingNetwork
        ? `${API_URL}/api/admin/blockchain-networks/${editingNetwork.id}`
        : `${API_URL}/api/admin/blockchain-networks`;
      
      const method = editingNetwork ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          chain_id: formData.chain_id ? parseInt(formData.chain_id) : null,
          withdrawal_fee_estimate: formData.withdrawal_fee_estimate ? parseFloat(formData.withdrawal_fee_estimate) : null,
          confirmation_blocks: parseInt(formData.confirmation_blocks)
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Operation failed');
      }

      toast.success(editingNetwork ? 'Network updated' : 'Network created');
      setShowModal(false);
      resetForm();
      fetchNetworks();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggle = async (networkId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain-networks/${networkId}/toggle`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to toggle');

      const data = await response.json();
      toast.success(data.message);
      fetchNetworks();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (networkId) => {
    if (!confirm('Are you sure you want to delete this network?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain-networks/${networkId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Network deleted');
      fetchNetworks();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blockchain-networks/seed-defaults`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!data.success) {
        toast.error(data.message);
        return;
      }

      toast.success(data.message);
      fetchNetworks();
    } catch (error) {
      toast.error('Failed to seed networks');
    }
  };

  const resetForm = () => {
    setFormData({
      network_id: '',
      name: '',
      display_name: '',
      chain_id: '',
      currency_symbol: '',
      explorer_url: '',
      icon: '🔷',
      withdrawal_fee_estimate: '',
      confirmation_blocks: 12,
      is_enabled: true
    });
    setEditingNetwork(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Blockchain Networks</h1>
            <p className="text-muted-foreground">Manage supported networks for crypto withdrawals</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSeedDefaults} variant="outline">
              <Database className="h-4 w-4 mr-2" />
              Seed Defaults
            </Button>
            <Button onClick={() => { setShowModal(true); resetForm(); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Network
            </Button>
          </div>
        </div>

        {/* Networks List */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-12">Loading...</div>
            ) : networks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No networks configured</p>
                <Button onClick={handleSeedDefaults}>Seed Default Networks</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {networks.map((network) => (
                  <div key={network.id} className="border rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{network.icon || '🔷'}</span>
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {network.name}
                          <Badge variant={network.is_enabled ? 'default' : 'secondary'}>
                            {network.is_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">{network.display_name}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Chain ID: {network.chain_id || 'N/A'} · Symbol: {network.currency_symbol}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggle(network.id)}
                      >
                        {network.is_enabled ? (
                          <><PowerOff className="h-4 w-4 mr-1" /> Disable</>
                        ) : (
                          <><Power className="h-4 w-4 mr-1" /> Enable</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingNetwork(network);
                          setFormData({
                            network_id: network.network_id,
                            name: network.name,
                            display_name: network.display_name,
                            chain_id: network.chain_id || '',
                            currency_symbol: network.currency_symbol,
                            explorer_url: network.explorer_url || '',
                            icon: network.icon || '🔷',
                            withdrawal_fee_estimate: network.withdrawal_fee_estimate || '',
                            confirmation_blocks: network.confirmation_blocks || 12,
                            is_enabled: network.is_enabled
                          });
                          setShowModal(true);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>{editingNetwork ? 'Edit Network' : 'Add Network'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Network ID *</Label>
                      <Input
                        value={formData.network_id}
                        onChange={(e) => setFormData({...formData, network_id: e.target.value})}
                        placeholder="e.g., polygon, ethereum"
                        required
                        disabled={!!editingNetwork}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="e.g., Polygon"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Display Name *</Label>
                    <Input
                      value={formData.display_name}
                      onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                      placeholder="e.g., Polygon · MATIC network"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Chain ID</Label>
                      <Input
                        type="number"
                        value={formData.chain_id}
                        onChange={(e) => setFormData({...formData, chain_id: e.target.value})}
                        placeholder="e.g., 137"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Currency Symbol *</Label>
                      <Input
                        value={formData.currency_symbol}
                        onChange={(e) => setFormData({...formData, currency_symbol: e.target.value})}
                        placeholder="e.g., MATIC"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Explorer URL</Label>
                    <Input
                      value={formData.explorer_url}
                      onChange={(e) => setFormData({...formData, explorer_url: e.target.value})}
                      placeholder="https://polygonscan.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Icon (Emoji)</Label>
                      <Input
                        value={formData.icon}
                        onChange={(e) => setFormData({...formData, icon: e.target.value})}
                        placeholder="🔷"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fee Estimate</Label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.withdrawal_fee_estimate}
                        onChange={(e) => setFormData({...formData, withdrawal_fee_estimate: e.target.value})}
                        placeholder="0.001"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_enabled}
                      onChange={(e) => setFormData({...formData, is_enabled: e.target.checked})}
                      className="h-4 w-4"
                    />
                    <Label>Enable this network</Label>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingNetwork ? 'Update' : 'Create'} Network
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
